import { useCallback, useMemo } from 'react';

import Fuse from 'fuse.js/min-basic';
import { uniqBy } from 'lodash-es';

import { useSearchPageData } from '#src/app/pages/search/pageContext';
import { SEARCH_LOCATION_SUGGESTIONS_MAX_COUNTRIES } from '#src/app/pages/search/shared/consts';

import type { RankedLocationItem } from '../../../../utils';
import { getMatchTier, normalizeLocationText } from '../../../../utils';
import { useCountryPetitionCounts } from '../useCountryPetitionCounts';

// common ways of typing a country that don't match its display name
const COUNTRY_ALIASES: Readonly<Record<string, string>> = {
	us: 'US',
	usa: 'US',
	america: 'US',
	'united states of america': 'US',
	uk: 'GB',
	britain: 'GB',
	'great britain': 'GB',
	england: 'GB',
	scotland: 'GB',
	wales: 'GB',
	'northern ireland': 'GB',
	uae: 'AE',
	holland: 'NL',
};

// low enough to drop near-misses like "columbus" -> Colombia (0.38), high enough to keep typos like "untied states" (0.27)
const FUZZY_THRESHOLD = 0.3;

// a bare 2-letter code ("la" -> Laos) ranks below city prefixes; common codes users mean (us, uk) are aliases instead
const CODE_MATCH_TIER = 2;
const FUZZY_MATCH_TIER = 4;

type Result = ModelHookResult<
	undefined,
	{
		searchForCountryName: (queryLocation: string, selectedLocation: string) => Promise<readonly RankedLocationItem[]>;
	}
>;

export function useSearchForCountryName(): Result {
	const {
		data: { countries },
	} = useSearchPageData();

	const {
		actions: { getCountryPetitionCounts },
	} = useCountryPetitionCounts();

	// indexed on normalized names so fuzzy matching ignores accents like the other match paths
	const fuseCountriesSet = useMemo(
		() =>
			new Fuse(
				countries.map(({ displayName }) => normalizeLocationText(displayName)),
				{ threshold: FUZZY_THRESHOLD },
			),
		[countries],
	);

	return {
		actions: {
			searchForCountryName: useCallback(
				async (queryLocation: string, selectedLocation: string) => {
					const normalizedQuery = normalizeLocationText(queryLocation);

					const counts = await getCountryPetitionCounts();
					// a country without petitions would always give zero results (e.g. "Anonymous Proxy")
					const searchableCountries = countries.filter(
						({ countryCode }) => !counts || !!counts[countryCode] || countryCode === selectedLocation,
					);

					const aliasCode = COUNTRY_ALIASES[normalizedQuery];
					const aliasMatches = searchableCountries.filter(({ countryCode }) => countryCode === aliasCode);
					const codeMatches =
						normalizedQuery.length === 2
							? searchableCountries.filter(({ countryCode }) => countryCode.toLowerCase() === normalizedQuery)
							: [];
					const nameMatches = searchableCountries.filter(({ displayName }) =>
						normalizeLocationText(displayName).includes(normalizedQuery),
					);
					// fuzzy matching only as a fallback, otherwise it adds noise (e.g. "ojai" -> Djibouti, Haiti, Jamaica)
					const fuzzyMatches =
						aliasMatches.length || codeMatches.length || nameMatches.length
							? []
							: fuseCountriesSet
									.search(normalizedQuery)
									.map(({ refIndex }) => countries[refIndex])
									.filter((country) => searchableCountries.includes(country));

					const candidates = [
						...aliasMatches.map((country) => ({ country, tier: 0 })),
						...codeMatches.map((country) => ({ country, tier: CODE_MATCH_TIER })),
						...nameMatches.map((country) => ({ country, tier: getMatchTier(country.displayName, normalizedQuery) })),
						...fuzzyMatches.map((country) => ({ country, tier: FUZZY_MATCH_TIER })),
					].sort(
						(a, b) =>
							a.tier - b.tier ||
							(counts?.[b.country.countryCode] ?? 0) - (counts?.[a.country.countryCode] ?? 0) ||
							a.country.displayName.length - b.country.displayName.length,
					);

					// deduping by name removes legacy aliases that share a display name (UK vs GB); when petition counts
					// are available the sort above keeps the one with petitions, otherwise the first in `countries` wins
					const uniqueResults = uniqBy(
						uniqBy(candidates, ({ country }) => country.countryCode),
						({ country }) => country.displayName,
					);

					return uniqueResults.slice(0, SEARCH_LOCATION_SUGGESTIONS_MAX_COUNTRIES).map(({ country, tier }) => ({
						value: country.countryCode,
						label: country.displayName,
						count: 0,
						isRefined: selectedLocation === country.countryCode,
						type: 'country' as const,
						tier,
					}));
				},
				[countries, fuseCountriesSet, getCountryPetitionCounts],
			),
		},
	};
}
